import { useState, useRef, useCallback } from "react";
import { useI18n, LANGS, type Lang } from "@/contexts/I18nContext";
import { Globe, Check } from "lucide-react";
import { AnimatePresence, m } from "framer-motion";
import { useClickOutside } from "@/hooks/useClickOutside";

const LABELS: Record<Lang, string> = { it: "IT", de: "DE", fr: "FR", en: "EN" };

export default function LanguageSwitcher({ compact = false }: { compact?: boolean }) {
  const { lang, setLang, t } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const handleClose = useCallback(() => setOpen(false), []);
  useClickOutside(ref, handleClose, open);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 rounded-full border border-border/60 px-3 py-1.5 text-xs font-semibold text-foreground hover:border-primary hover:text-primary transition-all ${
          compact ? "" : "md:px-3.5"
        }`}
        aria-label={t("site.a11y.language_switcher") || "Language"}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Globe className="w-3.5 h-3.5" aria-hidden="true" />
        <span>{LABELS[lang]}</span>
      </button>
      <AnimatePresence>
        {open && (
          <m.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.12 }}
            className="absolute right-0 mt-2 w-32 rounded-xl border border-border/40 bg-background/95 backdrop-blur-xl shadow-lg overflow-hidden z-50"
          >
            {LANGS.map((l) => (
              <button
                key={l}
                onClick={() => {
                  setLang(l);
                  setOpen(false);
                }}
                className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-primary/10 transition-colors ${
                  lang === l ? "text-primary" : "text-foreground"
                }`}
              >
                <span>{LABELS[l]}</span>
                {lang === l && <Check className="w-3.5 h-3.5" />}
              </button>
            ))}
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
}
